/**
 * Countdown shown on locked registration pages.
 *
 * Renders each unit as a number with its label stacked underneath, rather than
 * the old inline "0d 0h 13m 49s" run-on, which read as one long token.
 *
 * Takes the already-formatted `Xd Xh Xm Xs` string the four locked pages
 * (plan.$slug, plan.$slug.review, $schoolId, go.$slug) each build from their
 * own timer, so no page has to change how it computes the time.
 */
type Props = {
  /** Formatted as `0d 0h 13m 49s`. Null once the countdown has elapsed. */
  value: string | null;
  t: (key: string) => string;
};

export function RegistrationCountdown({ value, t }: Props) {
  // Null means registration has opened and the timer was cleared.
  if (!value) return null;

  const parts = value.match(/(\d+)d\s+(\d+)h\s+(\d+)m\s+(\d+)s/);

  // Unrecognised shape: show the raw string rather than an empty box.
  if (!parts) {
    return <div className="text-3xl font-bold font-heading tracking-wider">{value}</div>;
  }

  const units = [
    { amount: parts[1], label: t("unitDays") },
    { amount: parts[2], label: t("unitHours") },
    { amount: parts[3], label: t("unitMinutes") },
    { amount: parts[4], label: t("unitSeconds") },
  ];

  return (
    <div className="flex items-start justify-center gap-3 sm:gap-5">
      {units.map((unit) => (
        <div key={unit.label} className="flex min-w-[3.5rem] flex-col items-center">
          <span className="font-heading text-3xl font-bold leading-none tabular-nums sm:text-4xl">
            {unit.amount}
          </span>
          <span className="mt-1.5 text-[11px] font-medium uppercase tracking-wide opacity-70">
            {unit.label}
          </span>
        </div>
      ))}
    </div>
  );
}
